import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { healthsherpa } from './api';
import ClientPicker from './ClientPicker';
import { LogoTile } from './HealthSherpaPresent';
import { ROW_FONT_DEFAULT } from './HealthSherpaTable';   // 5.31.0: the list's own slider is gone (page-wide Text size)
import { AlertsBox, ExportsList, RefreshCard, UploadCard } from './HealthSherpaPolicy';
import PersonView, { personName } from './HealthSherpaPerson';
import ProfilePanel, { addressLine } from './HealthSherpaProfile';
import { carrierColor, carrierShortName, clientRowTone, highlightColors, highlightHoverColors, policyColors, rowTone } from './hsColors';
import { clientStatusBadge, displayName } from './hsLabels';
import { splitHighlight } from './hsHighlight';
import { fs } from './hsScale';   // 5.31.0: font sizes scale with the page's Text size

// =============================================================================
// HealthSherpa Clients (5.20.0; 5.20.1) — the searchable client database (replaces the 5.19.0 "Policy status"
// tab and its review list). ALL people / households, alphabetical by last name then first; a search box
// (debounced, at least 2 characters) finds a person by any member's name (dependents call in), phone, email,
// address / ZIP, policy number or the InsureDocs client (number or name) — never by HealthSherpa id.
// Filters + sort + page + the open person live in the ADDRESS BAR (pushState), so Back works.
// 5.20.1 (Henry 2026-10-10: "cleaner"): every row is ONE line of the same height (long text truncates with "…",
// the full text in the tooltip), ONE status badge (status · payment), short carrier names with a colour accent,
// no logos in collapsed rows; the table always fits the window (table-layout fixed, % column widths that total
// 100%; dragging a border trades width with the right-hand neighbour); a click / Enter / Space opens a DROP-DOWN
// with the full detail (Esc or a second click closes; one open at a time); the search words are highlighted
// (hsHighlight.js — plain React text nodes, never HTML).
// 5.20.4 (Henry 2026-10-11): zebra rows (hsColors.CLIENT_ROW, by the row's index on the page), a cleaner, lighter
// font scoped to this tab (HS_FONT; names 500, cells 400, headers 600, tabular figures in Premium / ZIP / Last
// refreshed / Year), and names shown in normal case (hsLabels.displayName — display only, stored data unchanged).
// 5.20.5 (Henry 2026-10-11: "we use the address if someone has the same name"): Street (address line 1) and City
// columns — sortable, searchable, highlighted, shown in normal case (hsLabels.displayAddress, display only).
// 5.22.0 (Henry 2026-10-12): TWO PANES. The list keeps Name · Address · Status · Carrier (Address = street, city ZIP from
// the export, else the linked InsureDocs client's address); every other column moved into the client profile panel
// (HealthSherpaProfile.jsx) that a row click opens on the right (50%, a draggable divider 30–75%, width remembered).
// "◀ Hide list" leaves ONLY that client on screen (the list, search box, counts, alerts and filters are not rendered).
// The Privacy button blurs names / addresses in the list and the alert lists and drops their hover titles; while the
// search box has focus and the shown rows ARE that search's result, they show unblurred. Opening a profile reads our
// own database only — never HealthSherpa.
// 5.27.4: the search text is NEVER in the address bar (or any URL — it would reach access logs and the Referer): it
// lives in this component's state only (no storage) and goes to the server in a POST body. Filters, sort, page, tab and
// the open person stay in the address bar, so Back still works. An old address with q= is cleaned on load.
// =============================================================================

const detailOf = (e) => e?.response?.data?.detail || e?.message || 'Something went wrong';
// 5.20.4: each name part through displayName (only an all-caps / all-lower name changes; mixed case stays as typed)
const fullName = (m) => [m.first, m.last].filter(Boolean).map(displayName).join(' ');
// "dependent JANE DOE" (the server's matched member) -> "dependent Jane Doe"
const matchedMember = (s) => String(s || '').replace(/^((?:spouse|dependent)\s+)?(.*)$/i, (m, rel, rest) => `${rel || ''}${rest.split(/\s+/).map(displayName).join(' ')}`);
// 5.20.4: the HealthSherpa Clients tab's own font (the list, its drop-down and the person page — not global CSS)
export const HS_FONT = '"Segoe UI", system-ui, -apple-system, Roboto, "Helvetica Neue", Arial, sans-serif';

// --- address-bar state ---------------------------------------------------------------------------------
// 5.27.4: no 'q' — the search text never goes into the address bar (readHsUrl ignores it, writeHsUrl never writes it).
export const URL_KEYS = ['carrier', 'status', 'payment', 'year', 'aor', 'assigned', 'changed', 'attention', 'renewal',
  'sort', 'dir', 'page', 'person'];
export const URL_EVENT = 'insuredocs-hs-url';

export function readHsUrl() {
  const out = {};
  try {
    const u = new URLSearchParams(window.location.search);
    URL_KEYS.forEach((k) => { const v = u.get(k); if (v) out[k] = v; });
  } catch (_e) { /* no URL access */ }
  return out;
}

// Writes the Clients state into the address bar (keeps ?healthsherpa=1, sets tab=clients).
export function writeHsUrl(next, { replace = false, state = null } = {}) {
  try {
    const u = new URLSearchParams();
    u.set('healthsherpa', '1');
    u.set('tab', 'clients');
    URL_KEYS.forEach((k) => {
      const v = next[k];
      if (v !== undefined && v !== null && v !== '' && v !== false) u.set(k, String(v));
    });
    const url = `${window.location.pathname}?${u.toString()}`;
    if (url !== `${window.location.pathname}${window.location.search}`) {
      if (replace) window.history.replaceState(state, '', url); else window.history.pushState(state, '', url);
    }
    window.dispatchEvent(new Event(URL_EVENT));
  } catch (_e) { /* history unavailable */ }
}

const listParams = (p) => {
  const o = {};
  ['carrier', 'status', 'payment', 'year', 'aor', 'assigned', 'changed', 'attention', 'renewal', 'sort', 'dir', 'page']
    .forEach((k) => { if (p[k]) o[k] = p[k]; });
  return o;
};

// --- columns: % of the table width, always 100 in total ----------------------------------------------------
// 5.22.0: four columns; Plan, Metal, Premium, City, ZIP, Agent of record, Last refreshed, Docs, Year and InsureDocs live in
// the profile panel. Address sorts by street (the server's existing sort key).
// 5.30.0: + Owner (the claim; an inactive owner reads as unclaimed — the server decides).
export const COLS = [
  { key: 'name', label: 'Name', sort: 'name', pct: 29, min: 15 },
  { key: 'address', label: 'Address', sort: 'street', pct: 30, min: 12 },
  { key: 'status', label: 'Status', sort: 'status', pct: 15, min: 8 },
  { key: 'carrier', label: 'Carrier', sort: 'carrier', pct: 13, min: 8 },
  { key: 'owner', label: 'Owner', sort: 'owner', pct: 13, min: 7 },
];
// 5.20.1: percentages (v1 held em widths); 5.20.5: v3 — 14 columns; 5.22.0: v4 — 4 columns; 5.30.0: v5 — 5 columns
// (older layouts are ignored)
const WIDTHS_KEY = 'insuredocs.hs.clientCols.v5';
// Tuned so the narrow columns (premium, ZIP, last refreshed, year, the Assign button) show their whole value at
// 1366px wide at the default text size; Name, Street, Plan and City take what is left and truncate first.
const round2 = (v) => Math.round(v * 100) / 100;
export const defaultPcts = () => COLS.map((x) => x.pct);
function loadPcts() {
  try {
    const s = JSON.parse(window.localStorage.getItem(WIDTHS_KEY) || 'null');
    if (Array.isArray(s) && s.length === COLS.length && s.every((v, i) => Number.isFinite(v) && v >= COLS[i].min)
        && Math.abs(s.reduce((a, b) => a + b, 0) - 100) < 0.05) return s.map(round2);
  } catch (_e) { /* storage unavailable */ }
  return defaultPcts();
}
const savePcts = (w) => { try { window.localStorage.setItem(WIDTHS_KEY, JSON.stringify(w)); } catch (_e) { /* storage unavailable */ } };
// Move the border right of column i by d % (the right-hand neighbour gives / takes the same amount).
export function tradePcts(w, i, d) {
  const total = w[i] + w[i + 1];
  const left = Math.min(total - COLS[i + 1].min, Math.max(COLS[i].min, w[i] + d));
  const next = w.slice();
  next[i] = round2(left);
  next[i + 1] = round2(total - left);
  return next;
}

const css = `
.hs-cl { border-collapse: collapse; table-layout: fixed; width: 100%; font-family: ${HS_FONT}; font-weight: 400; }
.hs-cl th { position: relative; font-weight: 600; font-size: 0.86em; text-align: left; padding: 0.45em 0.4em; vertical-align: bottom; line-height: 1.25; overflow-wrap: anywhere; }
.hs-cl th.num, .hs-cl td.num { text-align: right; }
.hs-cl th button.hs-sort { all: unset; cursor: pointer; }
.hs-cl th button.hs-sort:focus-visible { outline: 2px solid var(--hs-focus); outline-offset: 2px; }
.hs-cl tr.hs-row > td { height: 2.5em; padding: 0 0.4em; vertical-align: middle; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  border-bottom: 2px solid var(--hs-divider); line-height: 1.3; }
.hs-cl td.num, .hs-cl td.hs-tnum { font-variant-numeric: tabular-nums; }
.hs-cl tr.hs-row { background: var(--hs-panel); cursor: pointer; }
.hs-cl tr.hs-row.hs-alt { background: var(--hs-alt); }
.hs-cl .hs-name { font-weight: 500; }
.hs-cl tr.hs-row:hover, .hs-cl tr.hs-row:focus-visible, .hs-cl tr.hs-row.hs-open { background: var(--hs-hover); }
.hs-cl tr.hs-row:focus-visible { outline: 2px solid var(--hs-focus); outline-offset: -2px; }
.hs-cl tr.hs-row.hs-open > td { border-bottom-color: transparent; }
.hs-cl .hs-lite { color: var(--hs-subtext); font-weight: 400; }
.hs-cl .hs-badge { display: inline-block; max-width: 100%; box-sizing: border-box; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  vertical-align: middle; font-size: 0.82em; font-weight: 500; line-height: 1.55; padding: 0 0.5em; border-radius: 0.3em; border: 1px solid transparent; }
.hs-cl .hs-carrier { display: block; overflow: hidden; text-overflow: ellipsis; padding-left: 0.45em; border-left: 0.25em solid var(--hs-divider); }
.hs-cl .hs-link { all: unset; cursor: pointer; color: var(--hs-link); font-weight: 600; }
.hs-cl .hs-link:hover { text-decoration: underline; }
.hs-cl tr.hs-row .hs-link { font-weight: 400; }
.hs-cl .hs-btn { -webkit-appearance: none; appearance: none; font: inherit; font-size: 0.85em; line-height: 1.4; padding: 0 0.55em; border-radius: 0.3em; cursor: pointer;
  background: transparent; color: var(--hs-text); border: 1px solid var(--hs-divider); white-space: nowrap; max-width: 100%; overflow: hidden; text-overflow: ellipsis; }
.hs-cl .hs-btn:hover { background: var(--hs-panel); }
.hs-cl .hs-grip { position: absolute; top: 0; bottom: 0; right: -0.4em; width: 0.8em; cursor: col-resize; z-index: 2; touch-action: none; outline: none; }
.hs-cl .hs-grip::after { content: ''; position: absolute; top: 25%; bottom: 25%; left: calc(50% - 1px); width: 2px; border-radius: 1px; background: var(--hs-divider); }
.hs-cl .hs-grip:hover::after, .hs-cl .hs-grip:focus-visible::after { top: 0; bottom: 0; background: var(--hs-focus); }
.hs-cl mark.hs-mark { background: var(--hs-mark-bg); color: var(--hs-mark-text); padding: 0; border-radius: 0.15em; }
.hs-cl tr.hs-row:hover mark.hs-mark, .hs-cl tr.hs-row:focus-visible mark.hs-mark, .hs-cl tr.hs-row:focus-within mark.hs-mark,
.hs-cl tr.hs-row.hs-open mark.hs-mark, .hs-cl tr.hs-detail mark.hs-mark {
  background: var(--hs-mark-hover-bg); color: var(--hs-mark-hover-text); text-decoration: underline 1px; text-underline-offset: 0.15em; }
.hs-cl tr.hs-detail > td { padding: 0; border-bottom: 2px solid var(--hs-divider); background: var(--hs-hover); white-space: normal; }
.hs-cl .hs-detail-in { padding: 0.7em 0.9em 0.9em; animation: hs-open 160ms ease-out; }
@keyframes hs-open { from { opacity: 0; transform: translateY(-0.3em); } to { opacity: 1; transform: none; } }
.hs-cl .hs-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(17em, 1fr)); gap: 0.6em 1.6em; margin-top: 0.6em; }
.hs-cl .hs-kv { display: flex; gap: 0.6em; font-size: 0.92em; line-height: 1.45; }
.hs-cl .hs-kv > span:first-child { flex: none; width: 8.5em; color: var(--hs-subtext); }
.hs-cl .hs-sec { font-size: 0.8em; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--hs-subtext); margin-bottom: 0.2em; }
.hs-cl .hs-act { -webkit-appearance: none; appearance: none; font: inherit; font-size: 0.9em; padding: 0.3em 0.8em; border-radius: 0.3em; cursor: pointer;
  background: var(--hs-panel); color: var(--hs-text); border: 1px solid var(--hs-divider); }
.hs-cl .hs-act.hs-primary { background: #185FA5; color: #fff; border-color: #185FA5; }
.hs-cl .hs-act:disabled { opacity: 0.6; cursor: wait; }
/* 5.22.0: Privacy — personal cells are blurred (their titles are dropped in the markup, so no hover text either) */
.hs-cl td.hs-pii { filter: blur(5px); user-select: none; }
.hs-cl tr.hs-row.hs-open { box-shadow: inset 0.25em 0 0 var(--hs-focus); }
.hs-split-grip { flex: none; width: 8px; cursor: col-resize; touch-action: none; position: relative; outline: none; }
.hs-split-grip::after { content: ''; position: absolute; top: 0; bottom: 0; left: 3px; width: 2px; background: var(--hs-grip, #5a6370); }
.hs-split-grip:hover::after, .hs-split-grip:focus-visible::after { left: 2px; width: 4px; background: #4a8fd1; }
`;

const STATUS_OPTIONS = [['', 'Any status'], ['active', 'Active / effectuated'], ['pending', 'Pending'], ['cancelled', 'Cancelled'], ['terminated', 'Terminated'], ['other', 'Other / unknown']];
const PAYMENT_OPTIONS = [['', 'Any payment'], ['paid', 'Paid'], ['unpaid_first', 'Unpaid first payment'], ['past_due', 'Past due']];
const AOR_OPTIONS = [['', 'Any agent of record'], ['ours', 'Ours'], ['other', 'Another agent']];
const ASSIGNED_OPTIONS = [['', 'Assigned or not'], ['yes', 'Assigned'], ['no', 'Not assigned']];
const RENEWAL_OPTIONS = [['', 'Renewal or new'], ['yes', 'Renewal'], ['no', 'New (not a renewal)']];
const FILTER_KEYS = ['carrier', 'status', 'payment', 'year', 'aor', 'assigned', 'changed', 'attention', 'renewal'];

// --- small pieces ---------------------------------------------------------------------------------------------

// The matched search words, marked — React text nodes only (never HTML).
// 5.20.3: colours come from the table's CSS variables, so a hovered / focused / open row (and its drop-down) can
// switch to the stronger HIGHLIGHT_HOVER gold + underline.
export function Hl({ text, q }) {
  const s = text === null || text === undefined ? '' : String(text);
  if (!q) return s;
  const parts = splitHighlight(s, q);
  if (parts.length === 1 && !parts[0].mark) return s;
  return parts.map((p, i) => (p.mark
    ? <mark key={i} className="hs-mark" data-hs-mark="1">{p.text}</mark>
    : <React.Fragment key={i}>{p.text}</React.Fragment>));
}

// ONE status badge: hsLabels.clientStatusBadge (5.20.3: "<Status> · export" when only the export has a status;
// "Pending · Unpaid 1st payment").
export const statusBadge = (r) => clientStatusBadge(r, 'en');

function Badge({ text, tone, title, darkMode, outlined }) {
  const t = policyColors(tone || 'neutral', darkMode);
  // outlined (5.20.3): a lighter badge for a status taken from the export — no fill, the tone's border + text
  return <span className="hs-badge" title={title || text} data-outlined={outlined ? '1' : undefined}
    style={outlined ? { background: 'transparent', color: t.text, borderColor: t.border, fontWeight: 500 }
      : { background: t.bg, color: t.text, borderColor: t.border }}>{text}</span>;
}

// "Oct 10 8:30a" (Central)
export function compactCt(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
    .formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.month} ${p.day} ${p.hour}:${p.minute}${String(p.dayPeriod || '').toLowerCase().startsWith('p') ? 'p' : 'a'}`;
}

// Members after the name: the matched member first (so a dependent's match is visible on the one line).
function orderedMembers(r) {
  const list = (r.members || []).map((m) => ({ ...m, name: fullName(m) })).filter((m) => m.name);
  const hit = r.matched && r.matched.member ? r.matched.member.replace(/^(spouse|dependent)\s+/i, '').toLowerCase() : null;
  if (!hit) return list;
  return [...list.filter((m) => m.name.toLowerCase() === hit), ...list.filter((m) => m.name.toLowerCase() !== hit)];
}

// --- carrier filter with logos ------------------------------------------------------------------------------
function CarrierFilter({ c, value, carriers, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const cur = carriers.find((x) => x.name === value);
  const item = (active) => ({ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '6px 10px', background: active ? rowTone('hover', c.darkMode) : 'transparent',
    border: 'none', color: c.textPrimary, cursor: 'pointer', fontSize: fs(13), textAlign: 'left' });
  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} aria-haspopup="listbox" aria-expanded={open}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', fontSize: fs(13), background: c.inputBg, color: c.textPrimary,
          border: `1px solid ${value ? '#185FA5' : c.borderColor}`, borderRadius: 5, cursor: 'pointer', maxWidth: 260 }}>
        {cur && cur.logo_url ? <LogoTile url={cur.logo_url} name={cur.name} height={18} maxWidth={70} darkMode={c.darkMode} pad="1px 3px" /> : null}
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || 'Any carrier'}</span> ▾
      </button>
      {open && (
        <div role="listbox" style={{ position: 'absolute', zIndex: 30, top: '100%', left: 0, marginTop: 4, minWidth: 280, maxHeight: 360, overflowY: 'auto',
          background: c.panelBg, border: `1px solid ${c.borderColor}`, borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.25)' }}>
          <button role="option" aria-selected={!value} style={item(!value)} onClick={() => { onChange(''); setOpen(false); }}>Any carrier</button>
          {carriers.map((x) => (
            <button key={x.name} role="option" aria-selected={value === x.name} style={item(value === x.name)} onClick={() => { onChange(x.name); setOpen(false); }}>
              <span style={{ width: 74, flex: 'none' }}>{x.logo_url ? <LogoTile url={x.logo_url} name={x.name} height={20} maxWidth={70} darkMode={c.darkMode} pad="1px 3px" /> : null}</span>
              <span>{x.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Assign dialog (admin) ----------------------------------------------------------------------------------
export function AssignDialog({ c, ui, person, NewClientForm, onClose, onAssigned, onClientsChanged }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null);           // null | 'pick' | 'new'
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const pid = person.person_id || person.id;
  useEffect(() => {
    healthsherpa.policy.personSuggestions(pid).then((r) => setData(r.data)).catch((e) => setError(detailOf(e)));
  }, [pid]);
  const assign = async (clientId, source = 'manual') => {
    setBusy(true); setError(null);
    try {
      const r = await healthsherpa.policy.assignPerson(pid, clientId, source);
      setBusy(false);
      onAssigned(r.data);
    } catch (e) { setError(detailOf(e)); setBusy(false); }
  };
  return (
    <div role="dialog" aria-label="Assign to an InsureDocs client" style={{ position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '6vh' }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ width: 720, maxWidth: '94vw', maxHeight: '86vh', overflowY: 'auto', background: c.panelBg, color: c.textPrimary, borderRadius: 8,
        border: `1px solid ${c.borderColor}`, padding: 18, boxShadow: '0 10px 30px rgba(0,0,0,0.35)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <div style={{ fontSize: fs(16), fontWeight: 700, flex: 1 }}>Assign {personName(person)} to an InsureDocs client</div>
          <button style={ui.btn(c)} onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: fs(12), color: c.textSecondary, margin: '6px 0 10px' }}>
          Every HealthSherpa application of this person (all plan years) is linked to the client you choose.
        </div>
        {!data && !error && <div style={{ fontSize: fs(13), color: c.textSecondary }}>Looking for matching clients…</div>}
        {data && (
          <div>
            <div style={{ fontSize: fs(13), fontWeight: 600, marginBottom: 4 }}>
              {data.suggestions.length ? 'Suggested' : 'No client with this name was found'}
            </div>
            {data.suggestions.map((s) => (
              <div key={s.client_id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: fs(13), borderTop: `1px solid ${c.borderColor}` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{displayName(s.name)}{s.restricted ? ' 🔒' : ''}</div>
                  <div style={{ fontSize: fs(12), color: c.textSecondary }}>{s.address || '—'}</div>
                </div>
                <span style={{ fontSize: fs(12), color: c.textSecondary, whiteSpace: 'nowrap' }}>{s.reason}</span>
                <button style={ui.btn(c, true, busy)} disabled={busy} onClick={() => assign(s.client_id)}>Assign</button>
              </div>
            ))}
            {data.candidate_count > data.suggestions.length && (
              <div style={{ fontSize: fs(12), color: c.textSecondary }}>…{data.candidate_count - data.suggestions.length} more clients with this name — use Choose another</div>
            )}
          </div>
        )}
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button style={ui.btn(c)} onClick={() => setMode(mode === 'pick' ? null : 'pick')}>Choose another</button>
          <button style={ui.btn(c)} onClick={() => setMode(mode === 'new' ? null : 'new')}>+ New client</button>
        </div>
        {mode === 'pick' && <div style={{ marginTop: 8 }}><ClientPicker darkMode={c.darkMode} onPick={(r) => assign(r.id)} /></div>}
        {mode === 'new' && NewClientForm && data && (
          <NewClientForm c={c} initial={data.prefill} onCancel={() => setMode(null)}
            onPickExisting={(d) => assign(d.id)}
            onCreated={(nc) => { if (onClientsChanged) onClientsChanged([nc.id]); assign(nc.id, 'new_client'); }} />
        )}
        <ui.ErrorBox msg={error} />
      </div>
    </div>
  );
}

// --- one collapsed row ---------------------------------------------------------------------------------------------
// 5.22.0: Name · Address · Status · Carrier. A click / Enter / Space selects the row (the profile panel shows it).
// `blur` (Privacy on): the name and address cells get .hs-pii and NO title attribute, so nothing shows on hover.
function Row({ r, q, darkMode, selected, alt, blur, onSelect }) {
  const H = (t) => <Hl text={t} q={q} />;
  const members = orderedMembers(r);
  const st = statusBadge(r);
  const addr = addressLine(r);
  const carrierShort = carrierShortName({ issuer_id: r.issuer_id, issuer_name: r.carrier });
  const nameTitle = [personName(r), ...members.map((m) => `${m.name} (${m.relationship})`)].join(' · ')
    + (r.matched ? ` — matched: ${r.matched.member ? matchedMember(r.matched.member) : r.matched.label}` : '');
  return (
    <tr className={`hs-row${alt ? ' hs-alt' : ''}${selected ? ' hs-open' : ''}`} tabIndex={0} aria-selected={selected} data-testid="hs-clients-row" data-pid={r.person_id}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(); }
      }}>
      <td title={blur ? undefined : nameTitle} className={blur ? 'hs-pii' : undefined} data-col="name">
        <span className="hs-name">{H(personName(r))}</span>
        {members.length > 0 && (
          <span className="hs-lite">
            {members.map((m, i) => (
              <React.Fragment key={`${m.name}-${m.relationship}`}>
                {i === 0 ? ' · ' : ', '}{H(m.name)}
                {i === 0 && r.matched && r.matched.member && members[0].name.toLowerCase() === r.matched.member.replace(/^(spouse|dependent)\s+/i, '').toLowerCase()
                  ? <span style={{ fontStyle: 'italic' }}> ({r.matched.member.split(' ')[0]})</span> : null}
              </React.Fragment>
            ))}
          </span>
        )}
        {r.matched && !r.matched.member && r.matched.label !== 'name' && <span className="hs-lite" style={{ fontStyle: 'italic' }}> · matched: {r.matched.label}</span>}
      </td>
      <td title={blur ? undefined : addr} className={blur ? 'hs-pii' : undefined} data-col="address">{addr ? H(addr) : ''}</td>
      <td><Badge text={st.text} tone={st.tone} title={st.title} outlined={st.outlined} darkMode={darkMode} /></td>
      <td title={r.carrier || ''}>
        <span className="hs-carrier" style={{ borderLeftColor: carrierColor({ issuer_id: r.issuer_id, issuer_name: r.carrier }, darkMode) }}>
          {carrierShort}{r.changed_carrier ? <span className="hs-lite" title={r.switch ? `Switched from ${r.switch.from} (${r.switch.from_year})` : ''}> ⇄</span> : null}
        </span>
      </td>
      <td title={r.owner ? r.owner.name : 'Unclaimed'} data-col="owner">{r.owner ? r.owner.name : <span className="hs-lite">—</span>}</td>
    </tr>
  );
}

// --- 5.22.0: profile panel width + Privacy, remembered per browser ------------------------------------------------
export const PANEL_MIN = 30, PANEL_MAX = 75, PANEL_DEFAULT = 50;
const PANEL_KEY = 'insuredocs.hs.profilePanelPct.v1';
const PRIVACY_KEY = 'insuredocs.hs.privacy.v1';
export const clampPanel = (v) => Math.min(PANEL_MAX, Math.max(PANEL_MIN, Math.round(Number(v) * 10) / 10));
function loadPanelPct() {
  try { const v = Number(window.localStorage.getItem(PANEL_KEY)); if (Number.isFinite(v) && v > 0) return clampPanel(v); } catch (_e) { /* storage unavailable */ }
  return PANEL_DEFAULT;
}
const savePanelPct = (v) => { try { window.localStorage.setItem(PANEL_KEY, String(v)); } catch (_e) { /* storage unavailable */ } };
function loadPrivacy() { try { return window.localStorage.getItem(PRIVACY_KEY) === '1'; } catch (_e) { return false; } }
const savePrivacy = (on) => { try { window.localStorage.setItem(PRIVACY_KEY, on ? '1' : '0'); } catch (_e) { /* storage unavailable */ } };

// --- pagination ------------------------------------------------------------------------------------------------
function pageList(page, pages) {
  const set = new Set([1, pages, page - 2, page - 1, page, page + 1, page + 2].filter((p) => p >= 1 && p <= pages));
  const sorted = [...set].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => { if (i && p - sorted[i - 1] > 1) out.push('…'); out.push(p); });
  return out;
}

// --- the tab ---------------------------------------------------------------------------------------------------
export default function ClientsTab({ c, ui, isAdmin, openClientFolder, onClientsChanged, NewClientForm, onStartQuote, onOpenWorkspace }) {
  const [params, setParams] = useState(readHsUrl);
  // 5.27.4: the search text — component state only (never the address bar, never storage)
  const [qInput, setQInput] = useState('');
  const [searchQ, setSearchQ] = useState('');          // the debounced search the list was asked for
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);
  const [assignFor, setAssignFor] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [pcts, setPcts] = useState(loadPcts);
  const [personReload, setPersonReload] = useState(0);
  // 5.22.0: the profile panel (the row clicked), its width, Hide list, Privacy, search-box focus
  const [selRow, setSelRow] = useState(null);
  const [panelPct, setPanelPct] = useState(loadPanelPct);
  const [listHidden, setListHidden] = useState(false);
  const [privacy, setPrivacy] = useState(loadPrivacy);
  const [searchFocus, setSearchFocus] = useState(false);
  const splitRef = useRef(null);
  const latestPanel = useRef(panelPct);
  latestPanel.current = panelPct;
  const tableRef = useRef(null);
  const latestP = useRef(pcts);
  latestP.current = pcts;
  const darkMode = c.darkMode;

  // the address bar is the source of truth (Back / Forward, alerts that open a person)
  useEffect(() => {
    const sync = () => { setParams(readHsUrl()); };
    window.addEventListener('popstate', sync);
    window.addEventListener(URL_EVENT, sync);
    // 5.27.4: an older address (bookmark, history) may still carry q= — rewrite it without it, in place
    try { if (new URLSearchParams(window.location.search).has('q')) writeHsUrl(readHsUrl(), { replace: true }); } catch (_e) { /* ignore */ }
    return () => { window.removeEventListener('popstate', sync); window.removeEventListener(URL_EVENT, sync); };
  }, []);
  const go = useCallback((patch, opts) => {
    const next = { ...readHsUrl(), ...patch };
    if (!('page' in patch)) delete next.page;            // any filter / sort change goes back to page 1
    writeHsUrl(next, opts);
  }, []);

  // debounced search (at least 2 characters; empty clears it). A new search goes back to page 1.
  useEffect(() => {
    const v = qInput.trim();
    if (searchQ === v || (v.length === 1)) return undefined;
    const t = setTimeout(() => {
      setSearchQ(v);
      if (readHsUrl().page) go({}, { replace: true });
    }, 350);
    return () => clearTimeout(t);
  }, [qInput, searchQ, go]);

  const listKey = JSON.stringify({ ...listParams(params), q: searchQ || undefined });
  useEffect(() => {
    let alive = true;
    const p = JSON.parse(listKey);
    setLoading(true);
    healthsherpa.policy.people({
      q: p.q, carrier: p.carrier, status: p.status, payment: p.payment, plan_year: p.year, aor: p.aor, assigned: p.assigned,
      changed_carrier: p.changed ? 1 : undefined, attention: p.attention ? 1 : undefined, renewal: p.renewal,
      sort: p.sort || 'name', dir: p.dir || 'asc', page: Number(p.page) || 1, per_page: 50,
    }).then((r) => { if (alive) { setData({ ...r.data, shownQ: p.q || '' }); setError(null); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e?.response?.status === 403 ? "HealthSherpa Clients isn't open to staff yet." : detailOf(e)); setLoading(false); } });
    return () => { alive = false; };
  }, [listKey, reload]);
  // 5.22.0: a reloaded list (after Assign) refreshes the open profile's row in place
  useEffect(() => {
    if (!data || !selRow) return;
    const fresh = (data.rows || []).find((x) => x.person_id === selRow.person_id);
    if (fresh && fresh !== selRow) setSelRow(fresh);
  }, [data]);   // eslint-disable-line react-hooks/exhaustive-deps

  const selectRow = (r) => { setSelRow(r); setSearchFocus(false); };
  const closePanel = useCallback(() => {
    const id = selRow ? selRow.person_id : null;
    setSelRow(null); setListHidden(false);
    setTimeout(() => {
      const tr = id !== null && tableRef.current && tableRef.current.querySelector(`tr[data-pid="${id}"]`);
      if (tr) tr.focus();
    }, 0);
  }, [selRow]);
  // ✕ and Esc close the panel (not while the Assign dialog is open)
  useEffect(() => {
    if (!selRow) return undefined;
    const onKey = (e) => { if (e.key === 'Escape' && !assignFor) closePanel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [selRow, assignFor, closePanel]);

  // the divider between the list and the panel: drag (pointer) or ← / → (2%)
  const startSplitDrag = (e) => {
    if (e.button !== 0 || !splitRef.current) return;
    e.preventDefault();
    const rect = splitRef.current.getBoundingClientRect(), el = e.currentTarget;
    try { el.setPointerCapture(e.pointerId); } catch (_e) { /* old browser */ }
    const move = (ev) => { const v = clampPanel(((rect.right - ev.clientX) / rect.width) * 100); latestPanel.current = v; setPanelPct(v); };
    const up = () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); savePanelPct(latestPanel.current); };
    el.addEventListener('pointermove', move); el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
  };
  const keySplit = (e) => {
    const step = e.key === 'ArrowLeft' ? 2 : e.key === 'ArrowRight' ? -2 : 0;      // ← widens the panel
    if (!step) return;
    e.preventDefault();
    const v = clampPanel(panelPct + step);
    setPanelPct(v); savePanelPct(v);
  };
  const togglePrivacy = () => { const on = !privacy; setPrivacy(on); savePrivacy(on); };

  const openPerson = (id) => go({ person: String(id) }, { state: { fromList: true } });
  const backToList = () => {
    let fromList = false;
    try { fromList = !!(window.history.state && window.history.state.fromList); } catch (_e) { /* ignore */ }
    if (fromList) window.history.back(); else go({ person: '' });
  };
  const openClient = (id) => { if (openClientFolder) openClientFolder(id); };
  const assigned = (r) => {
    setAssignFor(null);
    setReload((k) => k + 1);
    setPersonReload((k) => k + 1);
    if (params.person && String(r.person_id) !== String(params.person)) go({ person: String(r.person_id) }, { replace: true });
  };

  // column resizing in % (the border trades width with the right-hand neighbour; the total stays 100)
  const startDrag = (i, e) => {
    if (e.button !== 0 || !tableRef.current) return;
    e.preventDefault(); e.stopPropagation();
    const startX = e.clientX, start = pcts, width = tableRef.current.getBoundingClientRect().width, el = e.currentTarget;
    try { el.setPointerCapture(e.pointerId); } catch (_e) { /* old browser */ }
    const move = (ev) => { const next = tradePcts(start, i, ((ev.clientX - startX) / width) * 100); latestP.current = next; setPcts(next); };
    const up = () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.removeEventListener('pointercancel', up); savePcts(latestP.current); };
    el.addEventListener('pointermove', move); el.addEventListener('pointerup', up); el.addEventListener('pointercancel', up);
  };
  const keyResize = (i, e) => {
    const step = e.key === 'ArrowRight' ? 0.5 : e.key === 'ArrowLeft' ? -0.5 : 0;
    if (!step) return;
    e.preventDefault();
    const next = tradePcts(pcts, i, step);
    setPcts(next); savePcts(next);
  };
  const widthsDefault = pcts.every((v, i) => v === COLS[i].pct);

  const facets = (data && data.facets) || { carriers: [], statuses: [], plan_years: [], current_years: [] };
  const yearValue = params.year || '';
  const yearOptions = useMemo(() => {
    const ys = new Set([...(facets.plan_years || []), ...(facets.current_years || [])]);
    return [...ys].sort((a, b) => b - a);
  }, [facets.plan_years, facets.current_years]);
  const anyFilter = FILTER_KEYS.some((k) => params[k]) || !!searchQ;
  const sel = (value, onChange, options, label) => (
    <select aria-label={label} value={value || ''} onChange={(e) => onChange(e.target.value)}
      style={{ ...ui.input(c, 'auto'), padding: '6px 8px', fontSize: fs(13), borderColor: value ? '#185FA5' : c.borderColor }}>
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
  const toggle = (key, label) => (
    <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: fs(13), cursor: 'pointer', whiteSpace: 'nowrap' }}>
      <input type="checkbox" checked={!!params[key]} onChange={(e) => go({ [key]: e.target.checked ? '1' : '' })} />{label}
    </label>
  );
  const sortBy = (key) => {
    const cur = params.sort || 'name';
    const dir = cur === key ? ((params.dir || 'asc') === 'asc' ? 'desc' : 'asc') : 'asc';
    go({ sort: key === 'name' && dir === 'asc' ? '' : key, dir: dir === 'asc' ? '' : dir });
  };
  const sortMark = (key) => ((params.sort || 'name') === key ? ((params.dir || 'asc') === 'asc' ? ' ▲' : ' ▼') : '');

  const tone = (k) => rowTone(k, darkMode);
  const vars = {
    '--hs-panel': clientRowTone('base', darkMode), '--hs-alt': clientRowTone('alt', darkMode), '--hs-hover': clientRowTone('hover', darkMode),
    '--hs-divider': clientRowTone('divider', darkMode), '--hs-text': tone('text'),
    '--hs-subtext': tone('subtext'), '--hs-focus': darkMode ? '#7d9cc0' : '#4a6489', '--hs-link': darkMode ? '#7fb2e5' : '#1f5d99',
    '--hs-mark-bg': highlightColors(darkMode).bg, '--hs-mark-text': highlightColors(darkMode).text,
    '--hs-mark-hover-bg': highlightHoverColors(darkMode).bg, '--hs-mark-hover-text': highlightHoverColors(darkMode).text,
  };

  // ---- the person page ----
  if (params.person) {
    return (
      <div style={{ fontFamily: HS_FONT }}>
        <PersonView personId={Number(params.person)} darkMode={darkMode} onBack={backToList} onOpenClient={openClient}
          onAssign={isAdmin ? (p) => setAssignFor(p) : null} reloadKey={personReload}
          onChanged={(id) => { setReload((k) => k + 1); if (String(id) !== String(params.person)) go({ person: String(id) }, { replace: true }); }} />
        {assignFor && <AssignDialog c={c} ui={ui} person={assignFor} NewClientForm={NewClientForm} onClose={() => setAssignFor(null)}
          onAssigned={assigned} onClientsChanged={onClientsChanged} />}
      </div>
    );
  }

  const rows = (data && data.rows) || [];
  const first = data ? (data.page - 1) * data.per_page + 1 : 0;
  const q = searchQ;
  // 5.22.0 Privacy: rows are blurred unless the search box has focus AND the rows on screen are that search's result
  // (the typed text, debounced, is what the list was loaded for) — so a half-typed search never unblurs the whole list.
  const typed = qInput.trim();
  const searchShowing = searchFocus && typed.length >= 2 && !!data && data.shownQ === typed && !loading;
  const blurRows = privacy && !searchShowing;
  const assignDialog = assignFor && <AssignDialog c={c} ui={ui} person={assignFor} NewClientForm={NewClientForm} onClose={() => setAssignFor(null)}
    onAssigned={assigned} onClientsChanged={onClientsChanged} />;
  const panel = selRow && (
    <ProfilePanel row={selRow} darkMode={darkMode} isAdmin={isAdmin} listHidden={listHidden}
      onToggleList={() => setListHidden(!listHidden)} onClose={closePanel} onOpenClient={openClient}
      onOpenFull={() => openPerson(selRow.person_id)} onAssign={isAdmin ? (x) => setAssignFor(x) : null}
      onStartQuote={onStartQuote} reloadKey={personReload}
      onOpenWorkspace={onOpenWorkspace} onOwnerChanged={() => setReload((k) => k + 1)} />
  );
  // Hide list: ONLY the profile is rendered — no list, search box, counts, alerts, filters or other client's data
  if (selRow && listHidden) {
    return (
      <div style={{ height: '100%', fontFamily: HS_FONT }} data-testid="hs-clients-hidden">
        {panel}
        {assignDialog}
      </div>
    );
  }
  const list = (
    <div style={{ padding: 18, display: 'grid', gap: 14, gridTemplateColumns: 'minmax(0, 1fr)', fontFamily: HS_FONT }} data-testid="hs-clients">
      <AlertsBox c={c} onOpenClient={openClient} onOpenPerson={openPerson} reloadKey={reload} privacy={privacy}
        onNeedsLinking={() => go({ assigned: 'no', year: '' })} />

      <div style={{ background: c.panelBg, border: `1px solid ${c.borderColor}`, borderRadius: 8, padding: 14 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <input type="search" value={qInput} onChange={(e) => setQInput(e.target.value)} data-testid="hs-clients-search"
            onFocus={() => setSearchFocus(true)} onBlur={() => setSearchFocus(false)}
            placeholder="Search a name (any household member), phone, email, address / ZIP, policy number or InsureDocs client"
            style={{ ...ui.input(c, 'auto'), flex: '1 1 420px', minWidth: 260, fontSize: fs(14),
              ...(privacy && !searchFocus && qInput ? { filter: 'blur(5px)' } : null) }} aria-label="Search clients" />
          <button onClick={togglePrivacy} aria-pressed={privacy} data-testid="hs-privacy"
            title={privacy ? 'Privacy is on: names and addresses in the list are blurred — click to show them' : 'Blur names and addresses in the list'}
            style={{ ...ui.btn(c, privacy), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <span aria-hidden="true">👁</span>{privacy ? 'Privacy on' : 'Privacy'}
          </button>
          {qInput.trim().length === 1 && <span style={{ fontSize: fs(12), color: c.textSecondary }}>type at least 2 characters</span>}
          {isAdmin && <button style={ui.btn(c)} onClick={() => setShowUpload(!showUpload)}>{showUpload ? 'Hide upload' : 'Upload export'}</button>}
        </div>
        {showUpload && isAdmin && <div style={{ marginTop: 10 }}><UploadCard c={c} ui={ui} onDone={() => setReload((k) => k + 1)} /></div>}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 10 }}>
          <CarrierFilter c={c} value={params.carrier || ''} carriers={facets.carriers || []} onChange={(v) => go({ carrier: v })} />
          {sel(params.status, (v) => go({ status: v }), STATUS_OPTIONS, 'Status')}
          {sel(params.payment, (v) => go({ payment: v }), PAYMENT_OPTIONS, 'Payment')}
          <select aria-label="Plan year" value={yearValue} onChange={(e) => go({ year: e.target.value })}
            style={{ ...ui.input(c, 'auto'), padding: '6px 8px', fontSize: fs(13), borderColor: yearValue ? '#185FA5' : c.borderColor }}>
            <option value="">Current plan year{facets.current_years && facets.current_years.length ? ` (${Math.min(...facets.current_years)})` : ''}</option>
            {yearOptions.map((y) => <option key={y} value={String(y)}>{y}</option>)}
            <option value="all">All years (latest of each)</option>
          </select>
          {sel(params.aor, (v) => go({ aor: v }), AOR_OPTIONS, 'Agent of record')}
          {sel(params.assigned, (v) => go({ assigned: v }), ASSIGNED_OPTIONS, 'Assigned')}
          {sel(params.renewal, (v) => go({ renewal: v }), RENEWAL_OPTIONS, 'Renewal')}
          {toggle('changed', 'Changed carrier')}
          {toggle('attention', 'Needs attention')}
          <button style={{ ...ui.btn(c, false, !anyFilter), padding: '5px 10px' }} disabled={!anyFilter}
            onClick={() => { setQInput(''); setSearchQ(''); writeHsUrl({ sort: params.sort, dir: params.dir }); }}>Clear filters</button>
        </div>
        <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', marginTop: 10, fontSize: fs(13) }}>
          <strong data-testid="hs-clients-count">
            {data ? (data.page > 1 ? `Showing ${first.toLocaleString()}–${(first + rows.length - 1).toLocaleString()} of ${data.total.toLocaleString()}`
              : `Showing ${rows.length.toLocaleString()} of ${data.total.toLocaleString()}`) : 'Loading…'}
          </strong>
          {data && data.plan_year !== 'all' && <span style={{ color: c.textSecondary }}>plan year {data.plan_year}</span>}
          {loading && data && <span style={{ color: c.textSecondary }}>updating…</span>}
          {data && data.other_years_total > 0 && (
            <button style={{ background: 'none', border: 'none', padding: 0, color: '#4a8fd1', cursor: 'pointer', fontSize: fs(13) }} onClick={() => go({ year: 'all' })}>
              {data.other_years_total} more {searchQ ? 'match' : ''} in other plan years — show all years
            </button>
          )}
          <span style={{ flex: 1 }} />
          {!widthsDefault && <button style={{ ...ui.btn(c), padding: '3px 8px', fontSize: fs(12) }} data-testid="hs-reset-columns"
            onClick={() => { try { window.localStorage.removeItem(WIDTHS_KEY); } catch (_e) { /* ignore */ } setPcts(defaultPcts()); }}>Reset columns</button>}
        </div>
      </div>

      <ui.ErrorBox msg={error} />
      {data && (
        <div style={{ overflowX: 'auto', background: tone('panel'), border: `1px solid ${c.borderColor}`, borderRadius: 8 }} data-testid="hs-clients-wrap">
          <style>{css}</style>
          <table ref={tableRef} className="hs-cl" data-testid="hs-clients-table" style={{ ...vars, fontSize: fs(ROW_FONT_DEFAULT), color: tone('text') }}>
            <colgroup>{COLS.map((x, i) => <col key={x.key} data-col={x.key} style={{ width: `${pcts[i]}%` }} />)}</colgroup>
            <thead>
              <tr style={{ color: tone('subtext'), borderBottom: `2px solid ${tone('headRule')}` }}>
                {COLS.map((x, i) => (
                  <th key={x.key} data-col={x.key} className={x.num ? 'num' : undefined}
                    aria-sort={(params.sort || 'name') === x.sort ? ((params.dir || 'asc') === 'asc' ? 'ascending' : 'descending') : 'none'}>
                    <button className="hs-sort" onClick={() => sortBy(x.sort)} title={`Sort by ${x.label}`}>{x.label}{sortMark(x.sort)}</button>
                    {i < COLS.length - 1 && (
                      <span className="hs-grip" role="separator" aria-orientation="vertical" tabIndex={0} aria-label={`Resize the ${x.label} column`}
                        onPointerDown={(e) => startDrag(i, e)} onKeyDown={(e) => keyResize(i, e)} onClick={(e) => e.stopPropagation()} />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={COLS.length} style={{ padding: '1.2em', color: tone('subtext') }}>
                  {searchQ ? 'No one matches this search with these filters.' : 'No one matches these filters.'}
                </td></tr>
              )}
              {rows.map((r, i) => (
                <Row key={r.person_id} r={r} q={q} darkMode={darkMode} alt={i % 2 === 1} blur={blurRows}
                  selected={!!selRow && selRow.person_id === r.person_id} onSelect={() => selectRow(r)} />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.pages > 1 && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: fs(13) }} data-testid="hs-clients-pages">
          <button style={ui.btn(c, false, data.page <= 1)} disabled={data.page <= 1} onClick={() => go({ page: String(data.page - 1) })}>‹ Prev</button>
          {pageList(data.page, data.pages).map((p, i) => (p === '…'
            ? <span key={`gap-${i}`} style={{ color: c.textSecondary }}>…</span>
            : <button key={p} style={{ ...ui.btn(c, p === data.page), minWidth: 36 }} aria-current={p === data.page ? 'page' : undefined}
              onClick={() => go({ page: p === 1 ? '' : String(p) })}>{p}</button>))}
          <button style={ui.btn(c, false, data.page >= data.pages)} disabled={data.page >= data.pages} onClick={() => go({ page: String(data.page + 1) })}>Next ›</button>
        </div>
      )}
      {isAdmin && (
        <div>
          <button style={{ background: 'none', border: 'none', color: c.textSecondary, cursor: 'pointer', fontSize: fs(13), padding: '4px 0' }} onClick={() => setShowTools(!showTools)}>
            {showTools ? '▾' : '▸'} Refresh &amp; uploaded exports (admin)
          </button>
          {showTools && (
            <div style={{ display: 'grid', gap: 14, marginTop: 8 }}>
              <RefreshCard c={c} ui={ui} planYears={facets.current_years && facets.current_years.length ? facets.current_years : null}
                onChanged={() => setReload((k) => k + 1)} />
              <ExportsList c={c} ui={ui} reloadKey={reload} />
            </div>
          )}
        </div>
      )}
    </div>
  );
  if (!selRow) return <>{list}{assignDialog}</>;
  // two panes: the list (scrolls on its own) · the divider · the profile panel (default 50%, 30–75%)
  return (
    <div ref={splitRef} style={{ display: 'flex', height: '100%', minHeight: 0, ...vars, '--hs-grip': c.borderColor }} data-testid="hs-clients-split">
      <style>{css}</style>
      <div style={{ width: `${100 - panelPct}%`, minWidth: 0, overflowY: 'auto' }} data-testid="hs-clients-listpane">{list}</div>
      <div className="hs-split-grip" role="separator" aria-orientation="vertical" tabIndex={0} aria-valuemin={PANEL_MIN} aria-valuemax={PANEL_MAX}
        aria-valuenow={panelPct} aria-label="Resize the profile panel (← / →)" data-testid="hs-split-grip"
        onPointerDown={startSplitDrag} onKeyDown={keySplit} />
      <div style={{ width: `${panelPct}%`, minWidth: 0, borderLeft: `1px solid ${c.borderColor}` }} data-testid="hs-clients-panelpane">{panel}</div>
      {assignDialog}
    </div>
  );
}

